import { diContainer, serviceNames } from '../diContainer/diContainer';
import TransactionRepository from '../repositories/TransactionRepository';
import ShippingRepository from '../repositories/ShippingRepository';
import SessionRepository from '../repositories/SessionRepository';
import Address from '../domain/entities/Address';
import { FIELD_TYPE } from '../constants/Constants';
import SHIPPING_METHODS from '../constants/ShippingMethods';

import Config from '../domain/entities/Config';

class SelectNewShippingAddressUseCase {
  private shippingRepository: ShippingRepository;

  private sessionRepository: SessionRepository;

  private transactionRepository: TransactionRepository;

  private config: Config;

  constructor() {
    this.shippingRepository = diContainer.get(serviceNames.shippingRepository);
    this.sessionRepository = diContainer.get(serviceNames.sessionRepository);
    this.transactionRepository = diContainer.get(serviceNames.transactionRepository);
    this.config = diContainer.get(serviceNames.config);
  }

  public async execute(): Promise<void> {
    const transaction = await this.transactionRepository.getTransaction();
    const shippingAddress: Address = new Address();
    const userAddress = this.sessionRepository.userAddress;
    const userZip = this.sessionRepository.userLocalStorageZip;
    const { isEstimatedDeliveryDateEnabled } = this.config;

    const { formStore } = this.shippingRepository;
    formStore.clearStore();
    formStore.id = Address.NEW_ADDRESS_ID;

    const country = this.config.shippingCountries?.find(countryItem => countryItem.default) || 
      this.config.shippingCountries?.find(countryItem => countryItem.value === this.config.countryId);
    if (country) {
      formStore.setFieldValue(FIELD_TYPE.COUNTRY, country.label || undefined);
      formStore.setFieldValue(FIELD_TYPE.COUNTRY_ID, country.value);
    }

    if (transaction?.user?.isProCustomer) {
      formStore.setFieldValue(FIELD_TYPE.FIRST_NAME, transaction.user.firstName);
      formStore.setFieldValue(FIELD_TYPE.LAST_NAME, transaction.user.lastName);
    }

    if (userAddress && transaction.selectedShippingMethodId === SHIPPING_METHODS.COURIER) {
      if (userAddress.locality) {
        formStore.setFieldValue(FIELD_TYPE.CITY, userAddress.locality);
      }
      if (userAddress.postal_code) {
        formStore.setFieldValue(FIELD_TYPE.ZIPCODE, userAddress.postal_code);
      }
      if (userAddress.administrative_area_level_1) {
        formStore.setFieldValue(FIELD_TYPE.STATE, userAddress.administrative_area_level_1);
      }
      if (userAddress.route) {
        const firstLineAddress = `${userAddress.street_number ? userAddress.street_number : ''} ${userAddress.route}`;
        formStore.setFieldValue(FIELD_TYPE.FIRST_LINE_ADDRESS, firstLineAddress);
      }
    } else if (userZip) {
      if (isEstimatedDeliveryDateEnabled) {
        this.shippingRepository.formStore.setFieldValue(FIELD_TYPE.ZIPCODE, userZip);
        this.getEstimatedDeliveryDate(userZip);
      }
    }
    // reload shipping methods if default country is different from previous saved transaction countryId
    const { [FIELD_TYPE.COUNTRY_ID]: countryId } = transaction?.shippingAddress.attributes;
    let shouldLoadShippingMethods = countryId &&
      this.config.countryId !== countryId && 
      transaction.selectedShippingMethodId !== SHIPPING_METHODS.COURIER;

    // update delivery options for multiple country delivery enabled site
    if (this.config.jsonSchemasEnabled && !countryId) {
      shouldLoadShippingMethods = true;
    }

    transaction.setShippingAddress(shippingAddress);
    
    if (shouldLoadShippingMethods) {
      await this.transactionRepository.loadShippingMethods(this.config.countryId);
    }
  }
  
  private async getEstimatedDeliveryDate(userZip: string): Promise<void> {
    const deliveryTabsDisabled = this.transactionRepository.transaction?.deliveryTabsDisabled;
    const isEstimatedDeliveryDateEnabled = this.config.isEstimatedDeliveryDateEnabled;
    const { getEstimatedDeliveryDate } = this.shippingRepository;
    if (isEstimatedDeliveryDateEnabled && !deliveryTabsDisabled) {
      const address = { [FIELD_TYPE.ZIPCODE]: userZip };
      this.transactionRepository.updateEstimatedDeliveryDateResponse(await getEstimatedDeliveryDate(address));
    }
  }
}

export default SelectNewShippingAddressUseCase;
